/**
 * OS PONTOS DE QUEBRA — o que a escala fluida NÃO resolve.
 *
 * `escala.ts` já fez a parte contínua: todo tamanho é `calc(var(--u) * N)` e
 * acompanha a janela, com piso em ~1040px e teto em ~2240px. Isso conserta
 * PROPORÇÃO — o botão que estourava em 1366 passa a caber.
 *
 * O que ela não conserta, e por isso este arquivo existe: abaixo de ~1040px a
 * unidade CONGELA (é o piso, e está certo — encolher fonte indefinidamente não
 * torna nada legível). A partir dali o que precisa mudar não é o tamanho, é o
 * ARRANJO: quatro colunas viram duas, as duas barras laterais deixam de dividir
 * a mesma linha, a tabela deixa de tentar caber e passa a rolar.
 *
 * As larguras não são redondas por gosto:
 *
 *   · 1440  a largura em que a Central foi desenhada (a âncora de `escala.ts`)
 *   · 1280  onde as DUAS barras + conteúdo ainda cabem lado a lado
 *   · 1024  o piso da escala fluida; abaixo daqui muda arranjo, não tamanho
 *   ·  768  tablet em pé: a barra da direita sai do fluxo e vira gaveta
 *   ·  480  telefone: tudo em uma coluna
 *
 * Uso, dentro de qualquer styled-component:
 *
 *   import { ate } from "../global/breakpoints";
 *   ${ate.tablet} { grid-template-columns: repeat(2, 1fr); }
 */

export const LARGURAS = {
  telefone: 480,
  tablet: 768,
  laptop: 1024,
  desktop: 1280,
  amplo: 1440,
} as const;

export type NomeDeLargura = keyof typeof LARGURAS;

const media = (tipo: "max-width" | "min-width") =>
  Object.fromEntries(
    Object.entries(LARGURAS).map(([nome, px]) => [
      nome,
      // `max-width` usa 0.02px a menos para não empatar com o `min-width` do
      // mesmo valor: em telas com DPR fracionário os dois casariam juntos.
      `@media (${tipo}: ${tipo === "max-width" ? px - 0.02 : px}px)`,
    ]),
  ) as Record<NomeDeLargura, string>;

/** `${ate.tablet} { … }` — vale de 0 até (a largura - 1). */
export const ate = media("max-width");

/** `${apartirDe.desktop} { … }` — vale da largura para cima. */
export const apartirDe = media("min-width");

/**
 * Alvo de toque mínimo. 44px é o mínimo do guia da Apple e do WCAG 2.5.5
 * (AAA); em `--u` ele acompanha a escala, mas com um piso em px de verdade —
 * um alvo que encolhe junto com a tela é exatamente o que não se quer no
 * monitor pequeno da loja.
 */
export const ALVO_DE_TOQUE = "max(calc(var(--u) * 44), 40px)";

/**
 * Regra de botão que NÃO pode truncar texto. Era o sintoma mais visível da
 * Central: os três botões de pergunta da IA e o "Detalhes" saíam cortados.
 */
export const BOTAO_LEGIVEL = `
  min-height: ${ALVO_DE_TOQUE};
  padding-inline: var(--sp-12);
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
`;
